import { css } from 'aphrodite';
import { SurveyRadioButton } from 'components/SurveyElements';
import { SurveyFieldType } from 'models/survey';
import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { ArrayUtils } from 'util/array';
import { checkIfTrue } from 'util/helpers';
import { style } from './style';

interface Props {
  formRegister: any;
  formErrors: Object;
  data: any;
}

export const AgreementForm: FC<Props> = ({ formRegister, formErrors, data }) => {
  const region = useSelector((state: RootState) => state.widget.cms.settings.exhibit_segment);
  const styles = style({ region });
  const hasRequiredFields: boolean = data.data.some((surveyField: any) => checkIfTrue(surveyField.required));

  const hasError = (element: any): boolean => {
    return Object.keys(formErrors).some((fieldWithError) => {
      return element.cms_ids.includes(fieldWithError);
    });
  };

  const getRows = (rows: any[]): JSX.Element[] => {
    return rows.map((surveyField: any, index: number) => {
      const required: boolean = checkIfTrue(surveyField.required);
      return (
        <div key={surveyField.cms_ids[0]} className={css(styles.row)}>
          <div className={css(styles.column, styles.descriptionColumn)}>
            {required && hasError(surveyField) && <div className={css(styles.required)}>{data.errors.required}</div>}
            <div className={css(styles.fieldDescriptionTitle, required && styles.fieldDescriptionTitleRequired)}>
              <span dangerouslySetInnerHTML={{ __html: surveyField.prompt }}></span>
            </div>
          </div>
          <div className={css(styles.optionContainer)}>{getSurveyFieldInputs(surveyField)}</div>
        </div>
      );
    });
  };

  const getSubsectionsRows = () => {
    if (!data.section?.subsections?.length) {
      return <div className={css(styles.rows)}>{getRows(data.data)}</div>;
    }

    return data.section.subsections.map((subsection: any) => {
      const subsectionFields = data.data.filter((field: any) => field.subsection === subsection.id);
      if (subsectionFields.length <= 0) return null;
      return (
        <>
          <div className={css(styles.subsectionTitle)}>{subsection.title}</div>
          <div className={css(styles.rows)}>
            {getRows(data.data.filter((field: any) => field.subsection === subsection.id))}
          </div>
        </>
      );
    });
  };

  const getSurveyFieldInputs = (surveyField: any): JSX.Element[] | JSX.Element => {
    switch (surveyField.type) {
      case SurveyFieldType.TEXT:
        return getTextInput(surveyField);
      case SurveyFieldType.RADIO:
      default:
        return getRadioInputs(surveyField);
    }
  };

  const getTextInput = (surveyField: any): JSX.Element => {
    const required: boolean = checkIfTrue(surveyField.required);
    const identifier: string = surveyField.cms_ids[0];
    return (
      <div className={css(styles.textInput)}>
        <input ref={formRegister({ required })} type='text' name={identifier} maxLength={40} />
      </div>
    );
  };

  const getRadioInputs = (surveyField: any): JSX.Element[] => {
    const required: boolean = checkIfTrue(surveyField.required);
    const hasCustomOptions =
      surveyField.custom_option_labels && ArrayUtils.removeEmptyStrings(surveyField.custom_option_labels).length;
    const options = hasCustomOptions
      ? surveyField.custom_option_labels
      : data.section.configuration.default_option_labels;
    const customStyles = style({ hasCustomOptions, region });
    return options.map((option: string, index: number) => {
      const identifier: string = surveyField.cms_ids[0];
      const value: number = 5 - index; // Expected posible values: 5, 4, 3, 2
      const fieldKey: string = `${identifier}.${value}`;
      return (
        <div key={fieldKey} className={css(customStyles.option)}>
          <span dangerouslySetInnerHTML={{ __html: option }}></span>
          <SurveyRadioButton
            forwardedRef={formRegister({ required })}
            id={fieldKey}
            name={identifier}
            value={value.toString()}
          />
        </div>
      );
    });
  };

  const getHeadlineColumns = (): JSX.Element[] => {
    const headlineColumns: JSX.Element[] = [];
    headlineColumns.push(
      <div key='description' className={css(styles.descriptionColumn, styles.tableTitle)}>
        {data.section.title}
      </div>
    );
    headlineColumns.push(...getHeadlineOptions());
    return headlineColumns;
  };

  const getHeadlineOptions = (): JSX.Element[] => {
    const options = data.section.configuration.default_option_labels || [];
    return options.map((option: string, index: number) => {
      return (
        <div key={index} className={css(styles.column, styles.headerOptionColumn)}>
          {option}
        </div>
      );
    });
  };

  const getFormDescription = (): React.ReactNode => {
    if (!data.section.description && !data.section.required_fields_label) return null;
    else
      return (
        <p>
          {data.section.description && <span>{data.section.description}</span>}
          {data.section.required_fields_label && (
            <span className={css(styles.required)}>{data.section.required_fields_label}</span>
          )}
        </p>
      );
  };

  return (
    <div className={css(styles.agreementForm)}>
      <div className={css(styles.description)}>{getFormDescription()}</div>
      <div className={css(styles.headline)}>{getHeadlineColumns()}</div>
      <div className={css(styles.content)}>{getSubsectionsRows()}</div>
    </div>
  );
};
